import styled, { css } from 'styled-components';
import hexToRgba from 'hex-rgba';
import { getStyle as get } from '@ids/ddms-styled/comps/dropMenu/intuit/light';
import { CheckmarkIcon, QBCheckmarkIcon } from '@ids/internal-icons';

// TODO: menu elevation in DD missing px unit
const elevation = '0 1px 4px 0 rgba(0, 0, 0, 0.2)';
const itemFontWeight = get('lineItem.fontWeight');
const itemFocusColor = get('highlight.color.focus');

// Quickbooks theme style
const menuQB = {
  paddingTop: '4px', // space01
  paddingBottom: '4px', // space01
  height: 'auto', // missing spec
  radius: '4px',
  backgroundColor: '#FFFFFF', // white
  border: '1px',
  borderColor: '#D4D7DC', // gray05
  shadow: `0 2px 2px 0 ${hexToRgba('#BABEC5', 0.3)}`
};

const lineItemQB = {
  fontStyle: 'normal',
  fontWeight: itemFontWeight,
  fontSize: '16px',
  lineHeight: '20px',
  color: '#393A3D', // gray01
  colorHover: '#FFFFFF', // white
  colorActive: '#FFFFFF', // white
  colorFocus: itemFocusColor,
  opacity: 100,
  opacityDisabled: 50,
  opacityFocus: 100,
  borderFocus: '3px',
  backgroundColor: '#FFFFFF', // white
  backgroundColorHover: '#2CA01C', // green02
  backgroundColorActive: '#108000', // green01
  paddingLeft: '8px', // space02
  paddingRight: '8px' // space02
};

const checkmarkQB = {
  color: '#2CA01C', // green02
  colorHover: '#FFFFFF', // white
  colorActive: '#FFFFFF', // white
  marginRight: '4px', // space01
  width: '9px',
  height: '9px;'
};

// Intuit theme style
const menuIN = {
  paddingTop: '4px', // TODO: missing in DD
  paddingBottom: '4px', // TODO: missing in DD
  height: get('menu.height'),
  radius: get('menu.radius'),
  backgroundColor: get('menu.background'),
  border: get('menu.border'),
  borderColor: get('menu.borderColor'),
  shadow: elevation
};

const lineItemIN = {
  fontStyle: get('lineItem.fontStyle'),
  fontWeight: itemFontWeight,
  fontSize: get('lineItem.fontSize'),
  lineHeight: get('lineItem.lineHeight'),
  color: get('lineItem.color.default'),
  colorHover: get('lineItem.color.hover'),
  colorActive: get('lineItem.color.active'),
  colorFocus: itemFocusColor,
  opacity: get('lineItem.opacity.default'),
  opacityDisabled: get('lineItem.opacity.disabled'),
  opacityFocus: get('highlight.opacity.focus'),
  borderFocus: '2px', // TODO: missing in DD
  backgroundColor: '#FFFFFF', // TODO: missing in DD
  backgroundColorHover: get('highlight.color.hover'),
  backgroundColorActive: get('highlight.color.active'),
  paddingLeft: '8px', // TODO: missing in DD
  paddingRight: '8px' // TODO: missing in DD
};

const checkmarkIN = {
  color: get('checkmark.color.default'),
  colorHover: get('checkmark.color.hover'),
  colorActive: get('checkmark.color.active'),
  marginRight: '8px', // TODO: missing in DD
  width: '10px',
  height: '8px'
};

// for mint, proconnect and turbotax use intuit style temporary
const checkmarkMT = { ...checkmarkIN, color: '#15EFE9' };
const checkmarkPC = { ...checkmarkIN };
const checkmarkTT = { ...checkmarkIN, color: '#06B6C9' };

// TODO: theme style data missing in DD
// for mint, proconnect and turbotax use intuit style temporary
const themes = {
  intuit: {
    menu: menuIN,
    lineItem: lineItemIN,
    checkmark: checkmarkIN
  },
  mint: {
    menu: menuIN,
    lineItem: lineItemIN,
    checkmark: checkmarkMT
  },
  proconnect: {
    menu: menuIN,
    lineItem: lineItemIN,
    checkmark: checkmarkPC
  },
  quickbooks: {
    menu: menuQB,
    lineItem: lineItemQB,
    checkmark: checkmarkQB
  },
  turbotax: {
    menu: menuIN,
    lineItem: lineItemIN,
    checkmark: checkmarkTT
  }
};

export const Wrapper = styled.div`
  ${props =>
    themes[props.theme] &&
    `
    border: ${themes[props.theme].menu.border} solid ${themes[props.theme].menu.borderColor};
    background: ${themes[props.theme].menu.backgroundColor};
    border-radius: ${themes[props.theme].menu.radius};
    box-shadow: ${themes[props.theme].menu.shadow};
    cursor: pointer;
    overflow-y: auto;
    height: ${themes[props.theme].menu.height};
    padding: ${themes[props.theme].menu.paddingTop} 0 ${themes[props.theme].menu.paddingBottom} 0;
  `}
  min-width: ${props => props.minWidth || get('menu.width')};
  ${props =>
    props.maxHeight &&
    `
    max-height: ${props.maxHeight};
  `};
`;

export const MenuItemContainer = styled.div`
  display: inline-block !important;
  height: 100%;
  ${props =>
    themes[props.theme] &&
    props.disabled &&
    `
    cursor: not-allowed;
    color: ${hexToRgba(
      themes[props.theme].lineItem.color,
      themes[props.theme].lineItem.opacityDisabled
    )};
  `}
  ${props =>
    themes[props.theme] &&
    (props.keyDown || props.selected) &&
    `
    color: ${themes[props.theme].lineItem.colorHover};
  `};
`;

export const CheckmarkBox = styled.div`
  ${props =>
    themes[props.theme] &&
    `
    width: ${Number(themes[props.theme].checkmark.width.slice(0, -2)) +
      Number(themes[props.theme].checkmark.marginRight.slice(0, -2))}px;
    box-sizing: border-box;
    display: inline-block;
    color: ${themes[props.theme].menu.backgroundColor};
  `};
`;

const checkmarkIconCss = css`
  ${props =>
    themes[props.theme] &&
    `
      fill: ${themes[props.theme].checkmark.color};
      width: ${themes[props.theme].checkmark.width};
      height: ${themes[props.theme].checkmark.height};
      &:hover{
        background-color: ${themes[props.theme].lineItem.backgroundColorHover};
      }
    `}
`;

export const CheckmarkIconWrapper = styled(CheckmarkIcon)`
  ${checkmarkIconCss}
`;

export const QBCheckmarkIconWrapper = styled(QBCheckmarkIcon)`
  ${checkmarkIconCss}
`;

export const MenuItemWrapper = styled.div`
  ${props =>
    themes[props.theme] &&
    `
    padding: ${themes[props.theme].lineItem.paddingRight} ${themes[props.theme].lineItem.paddingLeft};
    outline: none;
    font-size: ${themes[props.theme].lineItem.fontSize};
    color: ${themes[props.theme].lineItem.color};
    font-style: ${themes[props.theme].lineItem.fontStyle};
    font-weight: ${themes[props.theme].lineItem.fontWeight};
    line-height: ${themes[props.theme].lineItem.lineHeight};
    &:hover {
      background-color: ${themes[props.theme].lineItem.backgroundColorHover};
      color: ${themes[props.theme].lineItem.colorHover};
    }
  `}

  ${props =>
    themes[props.theme] &&
    !props.disabled &&
    `
    &:focus {
      box-shadow: inset 0 0 0 ${
        themes[props.theme].lineItem.borderFocus
      } ${hexToRgba(
      themes[props.theme].lineItem.colorFocus,
      themes[props.theme].lineItem.opacityFocus
    )};
    }
    &:active {
      background-color: ${themes[props.theme].lineItem.backgroundColorActive};
      box-shadow: none;
    }
    &:hover ${MenuItemContainer} {
      color: ${themes[props.theme].lineItem.colorHover};
    }
    &:hover ${CheckmarkIconWrapper}, &:hover ${QBCheckmarkIconWrapper} {
      fill: ${themes[props.theme].lineItem.colorHover};
    }
  `}

  ${props =>
    themes[props.theme] &&
    props.disabled &&
    `
    cursor: not-allowed;
    &:hover {
      background-color: ${themes[props.theme].lineItem.backgroundColor};
      color: ${themes[props.theme].lineItem.colorHover};
    }
  `}

  ${props =>
    themes[props.theme] &&
    props.keyDown &&
    `
    color: ${themes[props.theme].lineItem.colorActive};
  `};

  ${props =>
    themes[props.theme] &&
    props.selected &&
    `
    background-color: ${themes[props.theme].lineItem.backgroundColorActive};
  `};

  ${props =>
    themes[props.theme] &&
    props.highlighted &&
    !props.disabled &&
    `
    background-color: ${themes[props.theme].lineItem.backgroundColorHover};
    color: ${themes[props.theme].lineItem.colorHover};
    ${MenuItemContainer} {
      color: ${themes[props.theme].lineItem.colorHover};
    }
  `};
`;
